import { useCallback, useMemo, useState } from "react";
import { WORLD_MAP_SIZE, WORLD_MAP_LAND_PATH } from "../data/worldMapSvg";
import { GLOBE_REGIONS, latLngToMapXY } from "../data/globeRegions";
import PresenceIndiaMap from "./PresenceIndiaMap";
import PresenceRegionDetail from "./PresenceRegionDetail";

export default function PresenceGlobeMap() {
  const [activeRegion, setActiveRegion] = useState(null);
  const [detailVisible, setDetailVisible] = useState(false);

  const regionsWithCoords = useMemo(
    () => GLOBE_REGIONS.map((region) => ({
      ...region,
      ...latLngToMapXY(region.lat, region.lng),
    })),
    [],
  );

  const openRegion = useCallback((regionId) => {
    setActiveRegion(regionId);
    setDetailVisible(true);
  }, []);

  const resetToMap = useCallback(() => {
    setDetailVisible(false);
    setActiveRegion(null);
  }, []);

  return (
    <div className={`presence-globe-map${detailVisible ? " presence-globe-map--detail" : ""}`}>
      {!detailVisible && (
        <div className="presence-globe-map__map-wrap">
          <svg
            className="presence-globe-map__svg"
            viewBox={`0 0 ${WORLD_MAP_SIZE.width} ${WORLD_MAP_SIZE.height}`}
            preserveAspectRatio="xMidYMid meet"
            role="img"
            aria-label="World map showing Emicron presence in India, UAE, and South Africa"
          >
            <rect
              className="world-map-ocean"
              width={WORLD_MAP_SIZE.width}
              height={WORLD_MAP_SIZE.height}
            />
            <path className="world-map-land" d={WORLD_MAP_LAND_PATH} />
            {regionsWithCoords.map((region) => {
              const markerR = 8 * (region.markerScale || 1);
              return (
                <g
                  key={region.id}
                  className={`world-map-region world-map-region--${region.id}`}
                  onClick={() => openRegion(region.id)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      openRegion(region.id);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={`Explore ${region.label}`}
                >
                  <circle
                    className="world-map-marker-hit"
                    cx={region.x}
                    cy={region.y}
                    r={markerR + 10}
                  />
                  <circle
                    className="world-map-marker-pulse"
                    cx={region.x}
                    cy={region.y}
                    r={markerR + 4}
                    style={{ stroke: region.color }}
                  />
                  <circle
                    className="world-map-marker-ring"
                    cx={region.x}
                    cy={region.y}
                    r={markerR}
                    style={{ stroke: region.color }}
                  />
                  <circle
                    className="world-map-marker-core"
                    cx={region.x}
                    cy={region.y}
                    r={markerR * 0.55}
                    fill={region.color}
                  />
                  <text
                    className="world-map-marker-label"
                    x={region.x + markerR + 6}
                    y={region.y + 4}
                  >
                    {region.label}
                  </text>
                </g>
              );
            })}
          </svg>

          <p className="presence-globe-map__hint">
            Click a region on the map or below to explore
          </p>
          <div className="presence-globe-map__quick-picks" aria-label="Quick region picks">
            {GLOBE_REGIONS.map((region) => (
              <button
                key={region.id}
                type="button"
                className={`presence-globe-quick-pick presence-globe-quick-pick--${region.id}`}
                onClick={() => openRegion(region.id)}
              >
                <span
                  className="presence-globe-quick-pick__dot"
                  style={{ background: region.color }}
                />
                {region.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {detailVisible && activeRegion && (
        <div className="presence-globe-map__detail presence-globe-map__detail--visible">
          <button type="button" className="presence-globe-map__back" onClick={resetToMap}>
            ← Back to World Map
          </button>
          {activeRegion === "india" ? (
            <PresenceIndiaMap zoomed />
          ) : (
            <PresenceRegionDetail regionId={activeRegion} zoomed />
          )}
        </div>
      )}
    </div>
  );
}
